import React, { useEffect, useState } from "react";
import { Globe, ChevronDown } from "lucide-react";
import { safeStorage } from "@/lib/safeStorage";

/**
 * Globális nyelvválasztó: Google Translate widget alapján.
 * Auto-detektálja a felhasználó nyelvét, és lehetővé teszi a kézi váltást.
 * A fő nyelv MAGYAR (forrás).
 */

const LANGUAGES = [
  { code: "hu", label: "Magyar", flag: "🇭🇺" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "ro", label: "Română", flag: "🇷🇴" },
  { code: "sk", label: "Slovenčina", flag: "🇸🇰" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "hr", label: "Hrvatski", flag: "🇭🇷" },
  { code: "cs", label: "Čeština", flag: "🇨🇿" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "uk", label: "Українська", flag: "🇺🇦" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
];

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}

function setLangCookie(lang) {
  // Google Translate cookie format: /<source>/<target>
  const value = `/hu/${lang}`;
  document.cookie = `googtrans=${value}; path=/; domain=${window.location.hostname}`;
  document.cookie = `googtrans=${value}; path=/`;
}

function detectBrowserLang() {
  const nav = (navigator.language || navigator.userLanguage || "hu").toLowerCase();
  const base = nav.split("-")[0];
  if (LANGUAGES.find((l) => l.code === base)) return base;
  return "hu";
}

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("hu");

  useEffect(() => {
    // 1. Inject Google Translate script (once)
    if (!document.getElementById("google-translate-script")) {
      window.googleTranslateElementInit = function () {
        // eslint-disable-next-line no-new
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "hu",
            includedLanguages: LANGUAGES.map((l) => l.code).join(","),
            autoDisplay: false,
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          },
          "google_translate_element"
        );
      };
      const s = document.createElement("script");
      s.id = "google-translate-script";
      s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      document.body.appendChild(s);
    }

    // 2. Auto-detect + apply
    const saved = safeStorage.getItem("bv_lang");
    let initial = saved;
    if (!initial) {
      initial = detectBrowserLang();
      safeStorage.setItem("bv_lang", initial);
    }
    setCurrent(initial);
    if (initial !== "hu") {
      setLangCookie(initial);
      // Force reload only if cookie didn't already match
      const c = getCookie("googtrans");
      if (c !== `/hu/${initial}`) {
        // small delay to let Translate widget load
        setTimeout(() => {
          if (!window.location.hash.includes("googtrans")) {
            window.location.hash = `#googtrans(hu|${initial})`;
            window.location.reload();
          }
        }, 800);
      }
    }
  }, []);

  const change = (code) => {
    safeStorage.setItem("bv_lang", code);
    setCurrent(code);
    setOpen(false);
    if (code === "hu") {
      // Clear cookies + reload to original
      document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
      window.location.hash = "";
      window.location.reload();
    } else {
      setLangCookie(code);
      window.location.hash = `#googtrans(hu|${code})`;
      window.location.reload();
    }
  };

  const curLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <>
      {/* Hidden Google Translate mount point */}
      <div id="google_translate_element" style={{ display: "none" }} />

      <div className="relative notranslate" translate="no" data-testid="lang-switcher">
        <button
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition text-sm text-white/85"
          data-testid="lang-switcher-toggle"
          aria-label="Nyelv választása"
        >
          <Globe size={14} className="text-gold hidden sm:block" />
          <span className="text-base leading-none">{curLang.flag}</span>
          <span className="hidden md:inline xl:hidden 2xl:inline font-medium">{curLang.label}</span>
          <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <div className="absolute right-0 mt-2 w-56 max-h-[70vh] overflow-y-auto rounded-xl bg-[#0d0d0f] border border-white/10 shadow-2xl z-50 py-1.5">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => change(l.code)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-white/5 transition ${
                    l.code === current ? "text-gold bg-white/5" : "text-white/85"
                  }`}
                  data-testid={`lang-option-${l.code}`}
                >
                  <span className="text-lg leading-none">{l.flag}</span>
                  <span>{l.label}</span>
                  {l.code === current && <span className="ml-auto text-[10px] uppercase tracking-wider">aktív</span>}
                </button>
              ))}
              <div className="border-t border-white/5 mt-1 pt-2 px-3 pb-2 text-[10px] text-white/40 leading-relaxed">
                Az automatikus fordítást a Google Translate biztosítja. A pontosság néha eltérhet.
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
